import {
  approveConfigDraft,
  createConfigDraft,
  fetchControlPlaneState,
  publishConfigDraft,
  validateConfigDraft,
} from "../runtime-config/client";
import {
  RuntimeConfigRequestError,
  type ConfigDraft,
} from "../runtime-config/contracts";

export type SoundCatalogChangeResult =
  | { status: "approval_required"; draft: ConfigDraft }
  | { status: "published" };

type SoundCatalogChangeInput = {
  buildValues: (
    activeValues: Record<string, unknown>,
  ) => Record<string, unknown>;
  reason: string;
  ticketReference: string;
};

export async function submitSoundCatalogChange({
  buildValues,
  reason,
  ticketReference,
}: SoundCatalogChangeInput): Promise<SoundCatalogChangeResult> {
  const { controlState, draft } = await createValidatedSoundCatalogDraft({
    buildValues,
    reason,
    ticketReference,
  });

  if (controlState.payload.environment === "stg") {
    return { status: "approval_required", draft };
  }

  await approveAndPublishSoundCatalogDraft(draft);
  return { status: "published" };
}

export async function publishSoundCatalogChange(
  input: SoundCatalogChangeInput,
): Promise<void> {
  const { draft } = await createValidatedSoundCatalogDraft(input);
  await approveAndPublishSoundCatalogDraft(draft);
}

async function createValidatedSoundCatalogDraft({
  buildValues,
  reason,
  ticketReference,
}: SoundCatalogChangeInput) {
  const controlState = await fetchControlPlaneState();
  if (controlState.status !== "ok") {
    throw new Error(
      controlState.status === "unconfigured"
        ? "Runtime control plane is not configured."
        : controlState.status === "unauthorized"
          ? "Your verified identity cannot create runtime-config drafts."
          : controlState.message,
    );
  }

  const active = controlState.payload.active_release;
  let draft = await createConfigDraft({
    base_release_id: active?.id ?? null,
    values: buildValues(active?.values ?? {}),
    rules: active?.rules ?? [],
    reason,
    ticket_reference: ticketReference,
  });
  draft = await validateConfigDraft(draft);

  return { controlState, draft };
}

async function approveAndPublishSoundCatalogDraft(draft: ConfigDraft) {
  const approvedDraft = await approveConfigDraft(draft);
  await publishConfigDraft(approvedDraft);
}

export function formatSoundCatalogMutationError(error: unknown) {
  if (error instanceof RuntimeConfigRequestError) {
    if (error.code === "conflict") {
      return "The active release changed. Refresh Sound, review the rebased diff, and try again.";
    }
    if (error.code === "invalid") {
      return `The catalog draft failed validation: ${error.message}`;
    }
    if (error.code === "rate_limited") {
      return "The provider or control plane is rate limited. Retry after the displayed window.";
    }
  }
  return error instanceof Error ? error.message : "Catalog update failed.";
}
